/**
 * A collapsed section of a side panel, with its changed-this-chapter dot.
 *
 * `<details>` rather than a state hook, for the reasons `DmNotes.tsx` already argued and which
 * are stronger here: it is keyboard-operable without a keydown handler, announced as expandable
 * by a screen reader without ARIA, and — the one that matters on a panel of collapsed lists —
 * findable by the browser's own in-page search while closed. A `useState` disclosure is a
 * section whose contents do not exist to Ctrl-F.
 *
 * The dot is drawn only while the section is shut. "Cleared on open" and "not drawn when open"
 * are the same rule seen from two sides, and expressing it as the latter costs no state: the
 * player who has the section open is looking at the thing the dot was for.
 *
 * **It moved here from `features/character/` when the world codex's world panel arrived.** Two panels of
 * collapsed lists is the point at which a shared shape is cheaper than a copy, and the
 * alternative — the world feature importing a component out of the character feature — is the
 * kind of edge that looks harmless once and is a dependency graph by the fifth time. `marked`
 * is optional now because the world panel has no equivalent of the changed-this-chapter dot; `TODO.md` §4.4's
 * new-since-last-opened badge is the deferred item that would give it one.
 */
import { useState } from 'react';
import { useT } from '@/i18n/context';

interface SectionProps {
  title: string;
  /** Rendered beside the title. Null for a section whose size is not a number worth showing. */
  count?: number | null;
  /** The last turn touched this (`markedSections`). */
  marked?: boolean;
  /**
   * Opens the section from outside, for U6: a mention links to a card, and the card is inside
   * a section that is shut. Going false→true opens it and nothing else — the player can close
   * it again immediately afterwards, which a permanently-forced `open` would not allow.
   *
   * A controlled prop rather than a ref into the DOM, so the `+`/`−` glyph and the element
   * cannot disagree about what is open.
   */
  reveal?: boolean;
  children: React.ReactNode;
}

export function Section({
  title,
  count = null,
  marked = false,
  reveal = false,
  children,
}: SectionProps) {
  const t = useT();
  // Seeded from `reveal`, not from `false`: the panel is a `Dialog`, so a targeted section
  // *mounts* already revealed and there is no false→true edge to catch.
  const [open, setOpen] = useState(reveal);

  // React's "adjusting state when a prop changes" — compared during render rather than in an
  // effect, which would cascade a second render and which `react-hooks/set-state-in-effect`
  // objects to. Only the false→true edge opens it, so the player can close it again.
  const [revealed, setRevealed] = useState(reveal);
  if (reveal !== revealed) {
    setRevealed(reveal);
    if (reveal) setOpen(true);
  }

  return (
    <details
      open={open}
      className="border-rule border-t"
      onToggle={(event) => {
        setOpen(event.currentTarget.open);
      }}
    >
      <summary
        className="text-fg-muted hover:text-fg flex cursor-pointer list-none items-center gap-2 py-2.5 font-mono transition-colors"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        <span aria-hidden="true" className="text-fg-faint">
          {open ? '−' : '+'}
        </span>
        <span>{title}</span>
        {count !== null ? <span className="text-fg-faint tabular-nums">{count}</span> : null}
        {marked && !open ? (
          <span
            className="bg-accent ml-auto size-1.5 shrink-0 rounded-full"
            // The dot is decoration for a fact the text has to carry too, or a screen-reader
            // user is told a section exists and never told it changed.
            role="img"
            aria-label={t('ui.panel.changed_this_chapter')}
          />
        ) : null}
      </summary>

      <div className="pb-4">{children}</div>
    </details>
  );
}

/** Every section's empty state reads the same way, so they are written in one place. */
export function Nothing({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
      {children}
    </p>
  );
}
